import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { CheckIcon } from '@assets/icons/check';
import { DownloadIcon } from '@radix-ui/react-icons';
import { GerarMinutaResponse } from '@libs/types';
import { downloadBase64File, viewBase64File } from '@utils/base64';
import PdfViewer from '@components/PdfViewer';

const SucessContract = ({ dados }: { dados: GerarMinutaResponse }) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const iconSize = isMdUp ? 120 : 88;

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(id);
  }, []);

  return (
    <Box
      sx={{
        transition: 'transform 300ms ease, opacity 300ms ease',
        transform: mounted ? 'translateX(0)' : 'translateX(-20px)',
        opacity: mounted ? 1 : 0,
        padding: '16px 0px 0px 0px',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#00B90626',
          mb: '16px',
          border: '1px solid #00B906',
          borderRadius: '13px',
          padding: '32px 25px',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '24px',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            textAlign: { xs: 'center', md: 'left' },
            mb: '32px',
          }}
        >
          <CheckIcon width={iconSize} height={iconSize} />
          <Typography
            sx={{
              color: '#00B906',
              fontSize: { xs: '20px', md: '24px' },
              fontWeight: 700,
              maxWidth: { md: '20ch' },
            }}
          >
            Contratação realizada com sucesso!
          </Typography>
        </Box>
        <Typography
          sx={{
            color: '#808080',
            fontSize: '14px',
            mb: '24px',
          }}
        >
          {dados.businessMessage ||
            'Seu seguro garantia foi processado e está pronto para uso.'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            mb: '2px',
          }}
        >
          <Typography
            sx={{
              color: '#808080',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            Número da Apólice:
          </Typography>
          <Typography
            sx={{
              color: '#808080',
              fontSize: '14px',
            }}
          >
            {dados.numeroMinuta || '0123456789'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            mb: '2px',
          }}
        >
          <Typography
            sx={{
              color: '#808080',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            Contratado em:
          </Typography>
          <Typography
            sx={{
              color: '#808080',
              fontSize: '14px',
            }}
          >
            {new Date().toLocaleDateString('pt-BR')}
          </Typography>
        </Box>{' '}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            mb: '2px',
          }}
        >
          <Typography
            sx={{
              color: '#808080',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            Valor Segurado:
          </Typography>
          <Typography
            sx={{
              color: '#808080',
              fontSize: '14px',
            }}
          >
            {dados.valorIs
              ? `R$ ${dados.valorIs.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : 'R$ 0,00'}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '1fr 1fr',
          },
          gap: { xs: '16px', md: '20px' },
        }}
      >
        {/* Apólice */}
        <DocumentCard
          title="Apólice de Seguro"
          description="Documento oficial do seu seguro garantia."
          children={<PdfViewer base64={dados.base64Apolice!} />}
        />

        {/* Boleto único */}
        {dados.base64Boleto && !dados.base64Boletos?.length && (
          <DocumentCard
            title="Boleto para Pagamento"
            description="Forma de pagamento: boleto bancário."
            children={<PdfViewer base64={dados.base64Boleto} />}
          />
        )}

        {/* Múltiplos boletos (parcelado) */}
        {dados.base64Boletos?.map((b64) => {
          return (
            <DocumentCard
              title="Boleto para Pagamento"
              description="Forma de pagamento: boleto bancário."
              children={<PdfViewer base64={b64} />}
            />
          );
        })}

        {/* Placeholder para manter o layout antigo caso não haja boleto nenhum */}
        {!dados.base64Boleto && !dados.base64Boletos?.length && (
          <DocumentCard
            title="Boleto para Pagamento"
            description="Forma de pagamento: boleto bancário."
            onView={() => viewBase64File(dados.base64Boleto!)}
            onDownload={() =>
              downloadBase64File(
                dados.base64Boleto,
                `boleto-${dados.numeroMinuta}.pdf`,
              )
            }
          />
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: 'transparent',
          padding: { xs: '24px', md: '50px 108px 40px 88px' },
          borderRadius: '13px',
          my: '16px',
          border: '1px solid #004AA0',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '56px',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: '20px', md: '24px' },
              fontWeight: 700,
              mb: { xs: '24px', md: '24px' },
              color: '#004AA0',
            }}
          >
            Próximos passos:
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#808080',
            }}
          >
            1. Realize o pagamentos de boleto até as datas de vencimento;
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#808080',
            }}
          >
            2. Aguarde a confirmação por e-mail em até 2 horas úteis;
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#808080',
            }}
          >
            2. Use sua apólice na participação da licitação;
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: '#808080',
            }}
          >
            4. Em caso de dúvidas, entre em contato conosco.
          </Typography>
        </Box>
        <Box sx={{ paddingLeft: { md: '88px' } }}>
          <Typography
            sx={{
              fontSize: { xs: '20px', md: '24px' },
              mb: '24px',
              color: '#004AA0',
            }}
          >
            Contato:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              mb: '2px',
            }}
          >
            <Typography
              sx={{
                color: '#808080',
                fontSize: '14px',
                fontWeight: 700,
              }}
            >
              E-mail:
            </Typography>
            <Typography
              sx={{
                color: '#808080',
                fontSize: '14px',
              }}
            >
              cadastro@asasgarantias.com
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          padding: '40px 25px',
          borderRadius: '13px',
          border: '1px solid #FF9500',
          backgroundColor: '#FF950026',
          textAlign: { xs: 'left', md: 'center' },
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '14px',
            color: '#808080',
            mb: '24px',
          }}
        >
          IMPORTANTE
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#808080' }}>
          1. Guarde bem seus documentos
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#808080' }}>
          2. O pagamento deve ser feito até a data de vencimento
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#808080' }}>
          3. Em caso de dúvidas, entre em contato antes do vencimento
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#808080' }}>
          4. A apólice só tem validade após confirmação do pagamento
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: '#808080',
          mt: { xs: '40px', md: '56px' },
          mb: { xs: '12px', md: '16px' },
        }}
      />
      <Typography
        sx={{
          fontSize: { xs: '12px', md: '14px' },
          color: '#808080',
          textAlign: 'center',
          maxWidth: { xs: '40ch', md: '100%' },
          mx: 'auto',
        }}
      >
        Processamento seguro | Parceria BBMNET + ASAS Garantias
      </Typography>
    </Box>
  );
};

export default SucessContract;

function DocumentCard({
  title,
  description,
  onView,
  onDownload,
  children,
}: {
  title: string;
  description: string;
  onView?: () => void;
  onDownload?: () => void;
  children?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        backgroundColor: '#FFF',
        boxShadow: '0px 4px 4px 0px #00000040',
        padding: { xs: '24px', sm: '50px 88px 40px 88px' },
        borderRadius: '13px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '24px',
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: { xs: '20px', sm: '24px' },
            mb: '24px',
            color: '#004AA0',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#808080' }}>
          {description}
        </Typography>
      </Box>
      {children}
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'center', sm: 'start' },
          gap: '16px',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        {!children && (
          <>
            <Button
              variant="contained"
              onClick={onView}
              sx={{
                width: '100%',
                padding: { xs: '4px 14px', sm: '10.25px 16px' },
                color: 'white',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                background: '#808080',
                ':hover': { backgroundColor: '#808080ec' },
                maxWidth: { sm: '217px' },
                textTransform: 'none',
              }}
            >
              Visualizar
            </Button>
            <Button
              variant="contained"
              onClick={onDownload}
              sx={{
                width: '100%',
                color: 'white',
                fontFamily: 'Montserrat, sans-serif',
                padding: { xs: '4px 14px', sm: '10.25px 16px' },
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 700,
                maxWidth: { sm: '217px' },
                backgroundColor: '#004AA0',
              }}
            >
              Download
              <DownloadIcon
                style={{ marginLeft: '4px' }}
                height={18}
                width={17}
              />
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}
